import React from 'react';
import type { Room, SimilarMindsFirstQuestionHost } from '@deadpan/shared';
import { formatString } from '@deadpan/shared';
import type { Socket } from 'socket.io-client';
import type { ServerToClientEvents, ClientToServerEvents } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';
import { useSocket } from '../../contexts/SocketContext';

interface SimilarMindsConfigProps {
  socket: Socket<ServerToClientEvents, ClientToServerEvents> | null;
  currentRoom: Room;
  answerInputSeconds: number;
  votingSeconds: number;
  votesPerPlayer: number;
  maxVotesPerPlayer: number;
  firstQuestionHost: SimilarMindsFirstQuestionHost;
  onAnswerInputSecondsChange: (val: number) => void;
  onVotingSecondsChange: (val: number) => void;
  onVotesPerPlayerChange: (val: number) => void;
  onFirstQuestionHostChange: (val: SimilarMindsFirstQuestionHost) => void;
  onAnswerTimerEditStart?: () => void;
  onAnswerTimerEditEnd?: () => void;
  onVotingTimerEditStart?: () => void;
  onVotingTimerEditEnd?: () => void;
  configError: string | null;
}

const ANSWER_MIN = 0;
const ANSWER_MAX = 120;
const VOTING_MIN = 0;
const VOTING_MAX = 120;

export const SimilarMindsConfig: React.FC<SimilarMindsConfigProps> = ({
  socket,
  currentRoom,
  answerInputSeconds,
  votingSeconds,
  votesPerPlayer,
  maxVotesPerPlayer,
  firstQuestionHost,
  onAnswerInputSecondsChange,
  onVotingSecondsChange,
  onVotesPerPlayerChange,
  onFirstQuestionHostChange,
  onAnswerTimerEditStart,
  onAnswerTimerEditEnd,
  onVotingTimerEditStart,
  onVotingTimerEditEnd,
  configError,
}) => {
  const t = useTranslations();
  const { myPlayerId } = useSocket();
  const sm = t.games.similarMinds;

  const emitConfig = (
    answer: number,
    voting: number,
    votes: number,
    firstHost: SimilarMindsFirstQuestionHost = firstQuestionHost,
  ) => {
    if (!socket || currentRoom.hostId !== myPlayerId) return;
    socket.emit('client:update-similar-minds-config', answer, voting, votes, firstHost);
  };

  return (
    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-teal-50 border-2 border-teal-200 rounded-xl">
      <h3 className="text-base sm:text-lg font-bold text-teal-800 mb-3 sm:mb-4 flex items-center gap-2">
        <span>🤝</span>
        {sm.settingsTitle}
      </h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">{sm.firstQuestionHostLabel}</label>
          <p className="text-xs text-gray-500 mb-2">{sm.firstQuestionHostHint}</p>
          <select
            value={firstQuestionHost}
            onChange={(e) => {
              const val = e.target.value as SimilarMindsFirstQuestionHost;
              onFirstQuestionHostChange(val);
              emitConfig(answerInputSeconds, votingSeconds, votesPerPlayer, val);
            }}
            className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500 bg-white"
          >
            <option value="room_host">{sm.firstQuestionHostRoomHost}</option>
            <option value="random">{sm.firstQuestionHostRandom}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">{sm.answerTimerLabel}</label>
          <p className="text-xs text-gray-500 mb-2">{sm.answerTimerHint}</p>
          <input
            type="number"
            min={ANSWER_MIN}
            max={ANSWER_MAX}
            value={answerInputSeconds}
            onFocus={() => onAnswerTimerEditStart?.()}
            onBlur={() => onAnswerTimerEditEnd?.()}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              const parsed = Number.isNaN(val) ? 0 : val;
              onAnswerInputSecondsChange(parsed);
              emitConfig(parsed, votingSeconds, votesPerPlayer);
            }}
            className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">{sm.votingTimerLabel}</label>
          <p className="text-xs text-gray-500 mb-2">{sm.votingTimerHint}</p>
          <input
            type="number"
            min={VOTING_MIN}
            max={VOTING_MAX}
            value={votingSeconds}
            onFocus={() => onVotingTimerEditStart?.()}
            onBlur={() => onVotingTimerEditEnd?.()}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              const parsed = Number.isNaN(val) ? 0 : val;
              onVotingSecondsChange(parsed);
              emitConfig(answerInputSeconds, parsed, votesPerPlayer);
            }}
            className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">{sm.votesPerPlayerLabel}</label>
          <p className="text-xs text-gray-500 mb-2">
            {formatString(sm.votesPerPlayerHint, { max: String(maxVotesPerPlayer) })}
          </p>
          <input
            type="number"
            min={1}
            max={maxVotesPerPlayer}
            value={votesPerPlayer}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              const parsed = Number.isNaN(val) ? 1 : Math.max(1, Math.min(maxVotesPerPlayer, val));
              onVotesPerPlayerChange(parsed);
              emitConfig(answerInputSeconds, votingSeconds, parsed);
            }}
            className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500"
          />
        </div>
      </div>

      {configError && (
        <p className="mt-3 text-sm text-red-600 font-semibold">{configError}</p>
      )}
    </div>
  );
};
